import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { crmFetch } from './crmApi';
import { ACCOUNT_STATUSES } from './constants';

const EMPTY = { name: '', status: 'prospect', industry: '', website: '', location: '', notes: '', ownerUserId: '' };

export const AccountModal = ({ open, onOpenChange, onSaved, account, owners = [] }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!account;

  useEffect(() => {
    if (!open) return;
    setForm(account ? {
      name: account.name || '', status: account.status || 'prospect', industry: account.industry || '',
      website: account.website || '', location: account.location || '', notes: account.notes || '',
      ownerUserId: account.owner_user_id || '',
    } : EMPTY);
  }, [open, account]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Account name is required'); return; }
    setSaving(true);
    try {
      const body = JSON.stringify({ ...form, ownerUserId: form.ownerUserId || undefined });
      const res = isEdit
        ? await crmFetch(`/accounts/${account.id}`, { method: 'PUT', body })
        : await crmFetch('/accounts', { method: 'POST', body });
      if (!res.success) { toast.error(res.message || 'Failed to save account'); return; }
      toast.success(isEdit ? 'Account updated' : 'Account created');
      onOpenChange(false);
      onSaved && onSaved(res.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="account-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Account' : 'New Account'}</DialogTitle>
          <DialogDescription>Recruitment client company (separate from JobastiIQ software organizations).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Company Name *</Label>
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. PERI SE" data-testid="account-name-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Status</Label>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger data-testid="account-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACCOUNT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Industry</Label>
              <Input value={form.industry} onChange={(e) => set('industry', e.target.value)} data-testid="account-industry-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Location</Label>
              <Input value={form.location} onChange={(e) => set('location', e.target.value)} data-testid="account-location-input" />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Website</Label>
            <Input value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://…" data-testid="account-website-input" />
          </div>
          {owners.length > 1 && (
            <div>
              <Label className="mb-1.5 block">Account Owner</Label>
              <Select value={form.ownerUserId} onValueChange={(v) => set('ownerUserId', v)}>
                <SelectTrigger data-testid="account-owner-select"><SelectValue placeholder="Assign owner" /></SelectTrigger>
                <SelectContent>
                  {owners.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{`${o.first_name || ''} ${o.last_name || ''}`.trim()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="mb-1.5 block">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="account-notes-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="account-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="account-save-btn">
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Account')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
